import React, { useState, useEffect, useRef } from "react";

import { makeStyles } from "@material-ui/core/styles";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogTitle from "@material-ui/core/DialogTitle";
import Typography from "@material-ui/core/Typography";
import IconButton from "@material-ui/core/IconButton";
import Stack from "@mui/material/Stack";
import Box from "@mui/material/Box";
import { AddCircle, Delete } from "@mui/icons-material";

import { i18n } from "../../translate/i18n";
import FlowBuilderInteractionSettings, {
  DEFAULT_INTERACTION_SETTINGS,
  mergeInteractionSettings,
} from "../FlowBuilderInteractionSettings";

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    flexWrap: "wrap",
  },
  textField: {
    marginRight: theme.spacing(1),
    flex: 1,
  },
  btnWrapper: {
    position: "relative",
  },
  sectionBox: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
}));

const defaultSection = () => ({
  title: "Seção 1",
  rows: [{ id: "opt1", title: "Opção 1", description: "" }],
});

const FlowBuilderAddListModal = ({ open, onSave, onUpdate, data, close }) => {
  const classes = useStyles();
  const isMounted = useRef(true);

  const [activeModal, setActiveModal] = useState(false);
  const [message, setMessage] = useState("");
  const [buttonText, setButtonText] = useState("Escolha uma opção");
  const [sections, setSections] = useState([defaultSection()]);
  const [interactionSettings, setInteractionSettings] = useState(DEFAULT_INTERACTION_SETTINGS);
  const [labels, setLabels] = useState({
    title: "Adicionar lista ao fluxo",
    btn: "Adicionar",
  });

  useEffect(() => {
    if (open === "edit" && data?.data) {
      setLabels({ title: "Editar lista", btn: "Salvar" });
      setMessage(data.data.message ?? "");
      setButtonText(data.data.buttonText ?? "Escolha uma opção");
      const sec = data.data.sections;
      setSections(
        Array.isArray(sec) && sec.length > 0
          ? sec.map((s) => ({
              title: s.title || "Seção",
              rows: Array.isArray(s.rows)
                ? s.rows.map((r) => ({
                    id: r.id || "",
                    title: r.title || "",
                    description: r.description || "",
                  }))
                : [{ id: "opt1", title: "Opção 1", description: "" }],
            }))
          : [defaultSection()]
      );
      setInteractionSettings(mergeInteractionSettings(data.data));
      setActiveModal(true);
    } else if (open === "create") {
      setLabels({ title: "Adicionar lista ao fluxo", btn: "Adicionar" });
      setMessage("");
      setButtonText("Escolha uma opção");
      setSections([defaultSection()]);
      setInteractionSettings(DEFAULT_INTERACTION_SETTINGS);
      setActiveModal(true);
    } else {
      setActiveModal(false);
    }
  }, [open, data]);

  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  const handleClose = () => {
    close(null);
    setActiveModal(false);
  };

  const normalizeSections = () => {
    return sections.map((sec) => ({
      title: (sec.title || "Seção").trim().substring(0, 24),
      rows: (sec.rows || [])
        .filter((r) => r.id && String(r.id).trim() && r.title && String(r.title).trim())
        .map((r) => ({
          id: String(r.id).trim(),
          title: String(r.title).trim().substring(0, 24),
          description: r.description ? String(r.description).trim().substring(0, 72) : undefined,
        })),
    })).filter((sec) => sec.rows.length > 0);
  };

  const handleSave = () => {
    const normalized = normalizeSections();
    if (normalized.length === 0 || normalized.every((s) => s.rows.length === 0)) {
      return;
    }
    const payload = {
      message: message.trim() || "Escolha uma opção:",
      buttonText: buttonText.trim() || "Escolha uma opção",
      sections: normalized,
      ...interactionSettings,
    };
    if (open === "edit") {
      handleClose();
      onUpdate({ ...data, data: payload });
    } else {
      handleClose();
      onSave(payload);
    }
  };

  const updateSection = (sectionIndex, field, value) => {
    setSections((old) => {
      const next = [...old];
      if (!next[sectionIndex]) return old;
      next[sectionIndex] = { ...next[sectionIndex], [field]: value };
      return next;
    });
  };

  const updateRow = (sectionIndex, rowIndex, field, value) => {
    setSections((old) => {
      const next = old.map((sec, si) => {
        if (si !== sectionIndex || !sec.rows) return sec;
        const rows = [...sec.rows];
        if (!rows[rowIndex]) return sec;
        rows[rowIndex] = { ...rows[rowIndex], [field]: value };
        return { ...sec, rows };
      });
      return next;
    });
  };

  const addRow = (sectionIndex) => {
    setSections((old) => {
      const next = [...old];
      if (!next[sectionIndex]) return old;
      const rows = [...(next[sectionIndex].rows || [])];
      rows.push({ id: `opt${Date.now()}`, title: "Nova opção", description: "" });
      next[sectionIndex] = { ...next[sectionIndex], rows };
      return next;
    });
  };

  const removeRow = (sectionIndex, rowIndex) => {
    setSections((old) => {
      const next = old.map((sec, si) => {
        if (si !== sectionIndex || !sec.rows) return sec;
        if (sec.rows.length <= 1) return sec;
        return { ...sec, rows: sec.rows.filter((_, ri) => ri !== rowIndex) };
      });
      return next;
    });
  };

  const addSection = () => {
    setSections((old) => [...old, { title: `Seção ${old.length + 1}`, rows: [{ id: `r${Date.now()}`, title: "Item", description: "" }] }]);
  };

  const removeSection = (sectionIndex) => {
    if (sections.length <= 1) return;
    setSections((old) => old.filter((_, i) => i !== sectionIndex));
  };

  return (
    <div className={classes.root}>
      <Dialog
        open={activeModal}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        scroll="paper"
        style={{ zIndex: 3000 }}
        PaperProps={{ style: { zIndex: 3000, position: "relative" } }}
        BackdropProps={{
          style: { zIndex: 2999, backgroundColor: "rgba(0, 0, 0, 0.5)" },
        }}
      >
        <DialogTitle id="form-dialog-title">{labels.title}</DialogTitle>
        <Stack spacing={2} sx={{ px: 2, pb: 2 }}>
          <Typography variant="body2" color="textSecondary">
            Mensagem interativa tipo lista no WhatsApp (botão que abre a lista com seções e itens). Em outros canais é enviada como texto.
          </Typography>
          <TextField
            label="Texto da mensagem"
            rows={2}
            multiline
            variant="outlined"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            className={classes.textField}
            fullWidth
          />
          <TextField
            label="Texto do botão que abre a lista"
            variant="outlined"
            value={buttonText}
            onChange={(e) => setButtonText(e.target.value)}
            className={classes.textField}
            fullWidth
            placeholder="Ex: Escolha uma opção"
          />
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography>Seções e itens</Typography>
            <Button onClick={addSection} color="primary" variant="outlined" startIcon={<AddCircle />}>
              Adicionar seção
            </Button>
          </Stack>
          {sections.map((sec, si) => (
            <Box key={si} className={classes.sectionBox}>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                <TextField
                  size="small"
                  label="Título da seção"
                  value={sec.title || ""}
                  onChange={(e) => updateSection(si, "title", e.target.value)}
                  fullWidth
                />
                <IconButton onClick={() => removeSection(si)} disabled={sections.length <= 1} size="small">
                  <Delete />
                </IconButton>
              </Stack>
              {(sec.rows || []).map((row, ri) => (
                <Stack key={ri} direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                  <TextField
                    size="small"
                    label="ID"
                    value={row.id || ""}
                    onChange={(e) => updateRow(si, ri, "id", e.target.value)}
                    style={{ width: 100 }}
                  />
                  <TextField
                    size="small"
                    label="Título"
                    value={row.title || ""}
                    onChange={(e) => updateRow(si, ri, "title", e.target.value)}
                    style={{ width: 140 }}
                  />
                  <TextField
                    size="small"
                    label="Descrição (opcional)"
                    value={row.description || ""}
                    onChange={(e) => updateRow(si, ri, "description", e.target.value)}
                    style={{ flex: 1 }}
                  />
                  <IconButton
                    onClick={() => removeRow(si, ri)}
                    disabled={(sec.rows || []).length <= 1}
                    size="small"
                  >
                    <Delete />
                  </IconButton>
                </Stack>
              ))}
              <Button size="small" startIcon={<AddCircle />} onClick={() => addRow(si)}>
                Adicionar item
              </Button>
            </Box>
          ))}
        </Stack>
        <FlowBuilderInteractionSettings
          values={interactionSettings}
          onChange={setInteractionSettings}
        />
        <DialogActions>
          <Button onClick={handleClose} color="secondary" variant="outlined">
            {i18n.t("contactModal.buttons.cancel")}
          </Button>
          <Button
            color="primary"
            variant="contained"
            className={classes.btnWrapper}
            onClick={handleSave}
          >
            {labels.btn}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default FlowBuilderAddListModal;
